import { catchError, map, switchMap } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, filter, Observable, take, throwError } from 'rxjs';
import { Container, ContainerActivity } from '../_models';
import * as ContainerActions from '../_actions/container';
import { loadContainersSuccess } from '../_actions/container';
import * as ContainerActivityAction from '../_actions/containerActivity';
import { environment } from '../../../environments/environment';
import { AppState } from '../_reducers';
import { selectAllContainers } from '../_selectors/container.selectors';
import { WarehouseService } from './warehouse.service';
import { selectWarehousesLoaded } from '../_selectors/warehouse.selectors';

@Injectable()
export class ContainerService {
  private apiBaseUrl = environment.apiBaseUrl;
  private containerActivity: ContainerActivity = new ContainerActivity();
  // allContainers$ contains all initial containers from the API
  allContainers$: BehaviorSubject<Array<Container>> = new BehaviorSubject<Array<Container>>([]);
  // filteredContainers$ = filtered version of containersList$
  private filteredContainers$ = new BehaviorSubject<Container[]>([]);
  // containersList$ store observable
  containersList$: Observable<Container[]>;
  selectedDeposit: BehaviorSubject<number[]> = new BehaviorSubject<number[]>(null);

  private http = inject(HttpClient);
  private store = inject<Store<AppState>>(Store);
  private warehouseService = inject(WarehouseService);

  constructor() {
    this.containersList$ = this.store.select(selectAllContainers);

    // When we are filtering warehouse by intraFarm, we need to save allContainers$ with the containers filtered.
    this.store
      .select(selectWarehousesLoaded)
      .pipe(
        filter(Boolean), // wait until warehouses are loaded
        take(1), // only once
        switchMap(() => combineLatest([this.warehouseService.warehouseList$, this.allContainers$])),
        map(([warehouses, containers]) =>
          containers.filter((container) => warehouses.some((w) => w.id === container.warehouse.id))
        )
      )
      .subscribe((filteredContainers) => {
        // emit only if the data really changed
        const current = this.filteredContainers$.value;
        if (JSON.stringify(current) !== JSON.stringify(filteredContainers)) {
          this.store.dispatch(
            loadContainersSuccess({
              containers: filteredContainers ?? [],
            })
          );
          this.filteredContainers$.next(filteredContainers ?? []);
        }
      });
  }

  getContainersFromLocalStore(): Observable<Container[]> {
    return this.containersList$;
  }

  getContainers(): Observable<Container[]> {
    return this.http.get<Container[]>(this.apiBaseUrl + 'api/private/v1/containers').pipe(
      map((response) => {
        if (response) {
          this.allContainers$.next(response);
          return response;
        }
      })
    );
  }

  getContainer(id: number): Observable<Container> {
    return this.http
      .get<Container>(this.apiBaseUrl + 'api/private/v1/containers/' + id)
      .pipe(map((response) => response));
  }

  getLocalContainerById = (id: number): Container => {
    const list = this.allContainers$.value;
    const item = list.find((i) => i.id === id);
    return item ? JSON.parse(JSON.stringify(item)) : null;
  };

  getLocalContainerListByWarehouseId = (id: number): Container[] => {
    const list = this.allContainers$.value;
    return list.filter((f) => f.warehouse && f.warehouse.id === id);
  };

  getLocalContainerByName = (name: string): Container => {
    const list = this.allContainers$.value;
    return list.find((i) => i.name === name) || null;
  };

  saveContainer(container: Container): Observable<Container> {
    const mutableContainer: Container = { ...container };
    if (mutableContainer.warehouse) {
      const idwarehouse: any = {
        id: mutableContainer.warehouse.id,
      };
      mutableContainer.warehouse = idwarehouse;
    }

    delete mutableContainer.activities;
    delete mutableContainer.lastUpdated;
    delete mutableContainer.dateCreated;
    const companySelected = JSON.parse(localStorage.getItem('companySelected') || '{}');
    mutableContainer.companyId = companySelected ? companySelected.id : null;

    if (mutableContainer.id) {
      return this.http
        .put<Container>(
          this.apiBaseUrl + 'api/private/v1/containers/' + mutableContainer.id,
          mutableContainer
        )
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<Container>(this.apiBaseUrl + 'api/private/v1/containers/', mutableContainer)
        .pipe(
          map((response) => {
            if (response) {
              if (localStorage.getItem('tmpnewQua')) {
                const idcontainer: any = {
                  id: response.id,
                } as Container;

                this.containerActivity.activityDate = new Date();
                this.containerActivity.quantity = Number(localStorage.getItem('tmpnewQua'));
                this.containerActivity.type = 'buy';
                this.containerActivity.category = 'self_produced';
                this.containerActivity.container = idcontainer;

                this.store.dispatch(
                  ContainerActivityAction.addContainerActivity({
                    containerActivity: this.containerActivity,
                  })
                );
                localStorage.removeItem('tmpnewQua');
              }
              this.store.dispatch(ContainerActions.loadContainers());
              return response;
            }
            return null;
          })
        );
    }
  }

  deleteContainer(id: number): Observable<Container> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/containers/' + id).pipe(
      map(() => ({ id }) as Container),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  checkIfProductIdExistInContainer(product): Array<any> {
    const containers = this.allContainers$.value;
    const tmpContainer = [];

    containers.forEach((container) => {
      const sameProductId =
        container?.productId && product?.id && container.productId === product.id;
      const sameName =
        container?.name &&
        product?.name &&
        container.name.replace(/\s/g, '').toLowerCase() ===
          product.name.replace(/\s/g, '').toLowerCase();

      if (sameProductId || (!container.productId && sameName)) {
        tmpContainer.push(container);
      }
    });

    return tmpContainer;
  }
}
